/**
 * LBank WebSocket API
 * @see https://www.lbank.com/docs/#request-amp-subscription-instruction
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  LbankWsDepthMessage,
  LbankWsKlineMessage,
  LbankWsMessageBase,
  LbankWsPong,
  LbankWsSubscribeRequest,
  LbankWsTickerMessage,
  LbankWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const LBANK_WS_BASE_URL = 'wss://www.lbkex.net/ws/V2/';

/**
 * LBank WebSocket channel descriptors.
 */
export const LBANK_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'subscribe=depth&pair=<symbol>&depth=<levels>',
    supportsBatching: false,
    docUrl: 'https://www.lbank.com/docs/#market-depth',
  },
  ticker: {
    channel: 'tick',
    topic: 'subscribe=tick&pair=<symbol>',
    supportsBatching: false,
    docUrl: 'https://www.lbank.com/docs/#market',
  },
  klines: {
    channel: 'kbar',
    topic: 'subscribe=kbar&kbar=<interval>&pair=<symbol>',
    supportsBatching: false,
    docUrl: 'https://www.lbank.com/docs/#subscription-of-k-line-data',
  },
  trades: {
    channel: 'trade',
    topic: 'subscribe=trade&pair=<symbol>',
    supportsBatching: false,
    docUrl: 'https://www.lbank.com/docs/#trade-record',
  },
};

/**
 * LBank WebSocket topology information.
 */
export const LBANK_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: LBANK_WS_BASE_URL,
  singleConnectionPerChannel: true,
  heartbeatIntervalMs: 30000,
  note: 'Plain JSON messages (no compression). Server sends ping, client must respond pong within 1 minute. Orderbook sends full snapshots (not incremental). Symbol format: lowercase underscore (eth_btc).',
};

// ========================================
// Helper Types and Constants
// ========================================

export type LbankWsKlineInterval =
  | '1min'
  | '5min'
  | '15min'
  | '30min'
  | '1hr'
  | '4hr'
  | '8hr'
  | '12hr'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

export type LbankDepthLevel = '10' | '50' | '100';

export type LbankWsStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

// ========================================
// WebSocket Message Handling
// ========================================

/**
 * Check if message is a ping message from server.
 */
export function isLbankWsPing(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  if (candidate.action === 'ping') return true;
  if (candidate.action === undefined && 'ping' in candidate) return true;
  return false;
}

/**
 * Create pong response for LBank WebSocket.
 */
export function createLbankPong(pingValue: string): LbankWsPong {
  return {
    action: 'pong',
    pong: pingValue,
  };
}

/**
 * Create ping message for LBank WebSocket (client-initiated).
 */
export function createLbankPing(): { action: 'ping'; ping: string } {
  return {
    action: 'ping',
    ping: `${Date.now()}`,
  };
}

/**
 * Type guard for LBank WebSocket message base.
 */
export function isLbankWsMessage(value: unknown): value is LbankWsMessageBase {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<LbankWsMessageBase>;
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.pair === 'string' &&
    ['kbar', 'depth', 'trade', 'tick'].includes(candidate.type)
  );
}

/**
 * Type guard for LBank kline message.
 */
export function isLbankWsKlineMessage(value: unknown): value is LbankWsKlineMessage {
  if (!isLbankWsMessage(value)) return false;
  return value.type === 'kbar' && 'kbar' in value;
}

/**
 * Type guard for LBank depth message.
 */
export function isLbankWsDepthMessage(value: unknown): value is LbankWsDepthMessage {
  if (!isLbankWsMessage(value)) return false;
  return value.type === 'depth' && 'depth' in value;
}

/**
 * Type guard for LBank trade message.
 */
export function isLbankWsTradeMessage(value: unknown): value is LbankWsTradeMessage {
  if (!isLbankWsMessage(value)) return false;
  return value.type === 'trade' && 'trade' in value;
}

/**
 * Type guard for LBank ticker message.
 */
export function isLbankWsTickerMessage(value: unknown): value is LbankWsTickerMessage {
  if (!isLbankWsMessage(value)) return false;
  return value.type === 'tick' && 'tick' in value;
}

// ========================================
// Subscription Request Builders
// ========================================

/**
 * Create subscribe request for LBank depth channel.
 */
export function createLbankDepthSubscription(
  pair: string,
  depth: LbankDepthLevel = '100',
): LbankWsSubscribeRequest {
  return {
    action: 'subscribe',
    subscribe: 'depth',
    pair,
    depth,
  };
}

/**
 * Create subscribe request for LBank ticker channel.
 */
export function createLbankTickerSubscription(pair: string): LbankWsSubscribeRequest {
  return {
    action: 'subscribe',
    subscribe: 'tick',
    pair,
  };
}

/**
 * Create subscribe request for LBank kline channel.
 */
export function createLbankKlineSubscription(
  pair: string,
  interval: LbankWsKlineInterval,
): LbankWsSubscribeRequest {
  return {
    action: 'subscribe',
    subscribe: 'kbar',
    pair,
    kbar: interval,
  };
}

/**
 * Create subscribe request for LBank trade channel.
 */
export function createLbankTradeSubscription(pair: string): LbankWsSubscribeRequest {
  return {
    action: 'subscribe',
    subscribe: 'trade',
    pair,
  };
}

// ========================================
// WebSocket Subscription Options
// ========================================

export interface LbankWsSubscriptionOptions<T> {
  /** Subscription request message */
  subscribeMessage: LbankWsSubscribeRequest;
  /** Callback for successfully decoded messages */
  onMessage: (data: T) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: LbankWsStatus) => void;
  /** Base WebSocket URL */
  baseUrl?: string;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

/**
 * Create a generic LBank WebSocket subscription.
 */
function createLbankWsSubscription<T>(
  options: LbankWsSubscriptionOptions<T>,
): ReconnectingWebSocket {
  const {
    subscribeMessage,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = LBANK_WS_BASE_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');
    ws.send(JSON.stringify(subscribeMessage));

    if (pingInterval) clearInterval(pingInterval);
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(createLbankPing()));
      }
    }, LBANK_WS_TOPOLOGY.heartbeatIntervalMs);
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      const data = event.data as string;
      const payload = JSON.parse(data) as Record<string, unknown>;

      if (isLbankWsPing(payload)) {
        const pingValue = (payload.ping as string) || `${Date.now()}`;
        ws.send(JSON.stringify(createLbankPong(pingValue)));
        return;
      }

      if (payload.action === 'pong' || (payload.action === undefined && 'pong' in payload)) {
        return;
      }

      if (payload.status === 'ok' || payload.result === true) {
        return;
      }

      if (payload.status === 'error' || payload.error_code !== undefined) {
        const errorMsg =
          (payload.reason as string) || (payload['err-msg'] as string) || 'Unknown error';
        onError?.(new Error(`LBank subscription error: ${errorMsg}`));
        return;
      }

      if (isLbankWsMessage(payload)) {
        onMessage(payload as T);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`LBank WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  const originalClose = ws.close.bind(ws);
  ws.close = (code?: number, reason?: string) => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    originalClose(code, reason);
  };

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface LbankTickerSubscriptionOptions {
  symbol: string;
  onMessage: (ticker: LbankWsTickerMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: LbankWsStatus) => void;
}

export function subscribeLbankTicker(
  options: LbankTickerSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createLbankWsSubscription<LbankWsTickerMessage>({
    subscribeMessage: createLbankTickerSubscription(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Kline Subscription
// ========================================

export interface LbankKlineSubscriptionOptions {
  symbol: string;
  interval: LbankWsKlineInterval;
  onMessage: (kline: LbankWsKlineMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: LbankWsStatus) => void;
}

export function subscribeLbankKlines(
  options: LbankKlineSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = options;

  return createLbankWsSubscription<LbankWsKlineMessage>({
    subscribeMessage: createLbankKlineSubscription(symbol, interval),
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Depth Subscription
// ========================================

export interface LbankDepthSubscriptionOptions {
  symbol: string;
  levels?: LbankDepthLevel;
  onMessage: (depth: LbankWsDepthMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: LbankWsStatus) => void;
}

export function subscribeLbankDepth(options: LbankDepthSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, levels = '100', onMessage, onError, onStatusChange } = options;

  return createLbankWsSubscription<LbankWsDepthMessage>({
    subscribeMessage: createLbankDepthSubscription(symbol, levels),
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Trade Subscription
// ========================================

export interface LbankTradeSubscriptionOptions {
  symbol: string;
  onMessage: (trade: LbankWsTradeMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: LbankWsStatus) => void;
}

export function subscribeLbankTrades(
  options: LbankTradeSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createLbankWsSubscription<LbankWsTradeMessage>({
    subscribeMessage: createLbankTradeSubscription(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}
